/**
 * Data Validation Utilities
 * Functions for validating tea ceremony data structures and handling errors
 */

/**
 * Validates the structure and content of person data
 * @param {Array} data - Array of person objects to validate
 * @returns {Object} Validation result with isValid flag and errors array
 */
export function validatePersonData(data) {
  const errors = [];

  // Check if data is an array
  if (!Array.isArray(data)) {
    errors.push({
      field: 'root',
      message: 'People data must be an array',
      value: typeof data
    });
    return { isValid: false, errors };
  }

  // Validate each person object
  data.forEach((person, index) => {
    const personValidation = validatePerson(person, `person[${index}]`);
    if (!personValidation.isValid) {
      errors.push(...personValidation.errors);
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validates the structure and content of items data
 * @param {Object} data - Object mapping item names to connection arrays
 * @returns {Object} Validation result with isValid flag and errors array
 */
export function validateItemsData(data) {
  const errors = [];

  // Check if data is an object
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    errors.push({
      field: 'root',
      message: 'Items data must be an object',
      value: typeof data
    });
    return { isValid: false, errors };
  }

  // Validate each item and its connections
  Object.entries(data).forEach(([itemName, connections]) => {
    if (typeof itemName !== 'string' || itemName.trim() === '') {
      errors.push({
        field: `item.${itemName}`,
        message: 'Item name must be a non-empty string',
        value: itemName
      });
    }

    if (!Array.isArray(connections)) {
      errors.push({
        field: `item.${itemName}.connections`,
        message: 'Item connections must be an array',
        value: typeof connections
      });
    } else {
      connections.forEach((connection, index) => {
        const connectionValidation = validateItemConnection(connection, `item.${itemName}.connections[${index}]`);
        if (!connectionValidation.isValid) {
          errors.push(...connectionValidation.errors);
        }
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validates an individual person object
 * @param {Object} person - Person object to validate
 * @param {string} fieldPrefix - Prefix for field names in error messages
 * @returns {Object} Validation result with isValid flag and errors array
 */
export function validatePerson(person, fieldPrefix = 'person') {
  const errors = [];

  // Check if person is an object
  if (typeof person !== 'object' || person === null || Array.isArray(person)) {
    errors.push({
      field: fieldPrefix,
      message: 'Person must be an object',
      value: typeof person
    });
    return { isValid: false, errors };
  }

  // Required string fields
  const requiredStringFields = ['name_jp', 'name_romaji', 'years', 'region', 'description'];

  requiredStringFields.forEach(field => {
    if (!person[field] || typeof person[field] !== 'string' || person[field].trim() === '') {
      errors.push({
        field: `${fieldPrefix}.${field}`,
        message: `${field} is required and must be a non-empty string`,
        value: person[field]
      });
    }
  });

  // Validate years format - support ranges, BCE, centuries, and known tokens; reject arbitrary hyphenated strings
  if (person.years && typeof person.years === 'string') {
    const yearString = person.years.trim();

    const isValidYears = (s) => {
      if (s === '' ) return false;
      // Single year (e.g., 1600)
      if (/^\d{4}$/.test(s)) return true;
      // Open-ended range (e.g., 1981– or 1981-)
      if (/^\d{4}[–-]$/.test(s)) return true;
      // Unknown start (e.g., ?–1603)
      if (/^[?？][–-]\d{4}$/.test(s)) return true;
      // Closed range (e.g., 1522–1591)
      if (/^\d{4}[–-]\d{4}$/.test(s)) return true;
      // born/died patterns
      if (/^(born|died)\s\d{4}$/i.test(s)) return true;
      // BCE explicit (e.g., 500 BCE, 500 BCE–400 BCE)
      if (/^\d{1,4}\s?BCE(?:[–-]\d{1,4}\s?BCE)?$/i.test(s)) return true;
      // Japanese century notation, with or without BCE (e.g., 6世紀, 6世紀BCE, 6世紀?BCE)
      if (/^\d+世紀(?:\??\s?BCE)?$/i.test(s)) return true;
      // Era-like strings with kanji and a separator may be acceptable (very loose but requires non-ASCII)
      if (/[一-龥ぁ-んァ-ヶ]+[–-][一-龥ぁ-んァ-ヶ]+/.test(s)) return true;
      // Unknown/unspecified
      if (/^[?？]$/.test(s)) return true;
      return false;
    };

    if (!isValidYears(yearString)) {
      errors.push({
        field: `${fieldPrefix}.years`,
        message: 'Years field has an invalid format',
        value: person.years
      });
    }
  }

  // Required array fields
  const requiredArrayFields = ['connections', 'items'];

  requiredArrayFields.forEach(field => {
    if (!Array.isArray(person[field])) {
      errors.push({
        field: `${fieldPrefix}.${field}`,
        message: `${field} must be an array`,
        value: typeof person[field]
      });
    } else {
      // Validate array contents
      person[field].forEach((item, index) => {
        if (typeof item !== 'string' || item.trim() === '') {
          errors.push({
            field: `${fieldPrefix}.${field}[${index}]`,
            message: `${field} items must be non-empty strings`,
            value: item
          });
        }
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validates an individual item connection
 * @param {Object} connection - Connection object to validate
 * @param {string} fieldPrefix - Prefix for field names in error messages
 * @returns {Object} Validation result with isValid flag and errors array
 */
export function validateItemConnection(connection, fieldPrefix = 'connection') {
  const errors = [];

  // Check if connection is an object
  if (typeof connection !== 'object' || connection === null || Array.isArray(connection)) {
    errors.push({
      field: fieldPrefix,
      message: 'Connection must be an object',
      value: typeof connection
    });
    return { isValid: false, errors };
  }

  // Required string fields
  const requiredFields = ['person', 'connection'];

  requiredFields.forEach(field => {
    if (!connection[field] || typeof connection[field] !== 'string' || connection[field].trim() === '') {
      errors.push({
        field: `${fieldPrefix}.${field}`,
        message: `${field} is required and must be a non-empty string`,
        value: connection[field]
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validates that person references in items data actually exist in people data
 * @param {Array} people - Array of person objects
 * @param {Object} items - Items data object
 * @returns {Object} Validation result with isValid flag and errors array
 */
export function validateDataIntegrity(people, items) {
  const errors = [];

  // Create a set of all person names for quick lookup
  const personNames = new Set();

  if (Array.isArray(people)) {
    people.forEach(person => {
      if (person.name_jp) personNames.add(person.name_jp);
      if (person.name_romaji) personNames.add(person.name_romaji);
    });
  }

  // Check if items reference valid people
  if (typeof items === 'object' && items !== null) {
    Object.entries(items).forEach(([itemName, connections]) => {
      if (Array.isArray(connections)) {
        connections.forEach((connection, index) => {
          if (connection.person && typeof connection.person === 'string') {
            // Check if the referenced person exists
            if (!personNames.has(connection.person)) {
              errors.push({
                field: `item.${itemName}.connections[${index}].person`,
                message: `Referenced person "${connection.person}" does not exist in people data`,
                value: connection.person
              });
            }
          }
        });
      }
    });
  }

  // Check if people reference valid items
  if (Array.isArray(people)) {
    const itemNames = new Set(Object.keys(items || {}));

    people.forEach((person, personIndex) => {
      if (Array.isArray(person.items)) {
        person.items.forEach((itemName, itemIndex) => {
          if (!itemNames.has(itemName)) {
            errors.push({
              field: `person[${personIndex}].items[${itemIndex}]`,
              message: `Referenced item "${itemName}" does not exist in items data`,
              value: itemName
            });
          }
        });
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Sanitizes and cleans data to remove common issues
 * @param {Object|Array} data - Data to sanitize
 * @returns {Object|Array} Sanitized data
 */
export function sanitizeData(data) {
  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item));
  }

  if (typeof data === 'object' && data !== null) {
    const sanitized = {};

    Object.entries(data).forEach(([key, value]) => {
      // Trim string values
      if (typeof value === 'string') {
        sanitized[key] = value.trim();
      }
      // Recursively sanitize nested objects/arrays
      else if (typeof value === 'object') {
        sanitized[key] = sanitizeData(value);
      }
      else {
        sanitized[key] = value;
      }
    });

    return sanitized;
  }

  return data;
}

/**
 * Validates data size and performance constraints
 * @param {Array} people - People data array
 * @param {Object} items - Items data object
 * @returns {Object} Validation result with warnings about performance
 */
export function validateDataSize(people, items) {
  const warnings = [];

  // Check people array size
  if (Array.isArray(people)) {
    if (people.length > 1000) {
      warnings.push({
        field: 'people',
        message: `Large dataset detected: ${people.length} people. Consider implementing pagination or virtualization.`,
        severity: 'warning'
      });
    }

    if (people.length > 5000) {
      warnings.push({
        field: 'people',
        message: `Very large dataset: ${people.length} people. Performance may be impacted.`,
        severity: 'error'
      });
    }
  }

  // Check items object size
  if (typeof items === 'object' && items !== null) {
    const itemCount = Object.keys(items).length;
    const connectionCount = Object.values(items).reduce((total, connections) => {
      return total + (Array.isArray(connections) ? connections.length : 0);
    }, 0);

    if (itemCount > 500) {
      warnings.push({
        field: 'items',
        message: `Large number of items: ${itemCount}. Consider optimizing data structure.`,
        severity: 'warning'
      });
    }

    if (connectionCount > 2000) {
      warnings.push({
        field: 'items.connections',
        message: `Large number of connections: ${connectionCount}. Performance may be impacted.`,
        severity: 'warning'
      });
    }
  }

  return {
    isValid: warnings.filter(w => w.severity === 'error').length === 0,
    warnings
  };
}

/**
 * Creates a detailed validation report for debugging
 * @param {Array} people - People data array
 * @param {Object} items - Items data object
 * @returns {Object} Comprehensive validation report
 */
export function createValidationReport(people, items) {
  const report = {
    timestamp: new Date().toISOString(),
    peopleValidation: validatePersonData(people),
    itemsValidation: validateItemsData(items),
    integrityValidation: validateDataIntegrity(people, items),
    sizeValidation: validateDataSize(people, items),
    summary: {}
  };

  // Create summary
  const totalErrors = [
    ...report.peopleValidation.errors,
    ...report.itemsValidation.errors,
    ...report.integrityValidation.errors
  ];

  const totalWarnings = report.sizeValidation.warnings || [];

  report.summary = {
    isValid: totalErrors.length === 0,
    errorCount: totalErrors.length,
    warningCount: totalWarnings.length,
    peopleCount: Array.isArray(people) ? people.length : 0,
    itemsCount: typeof items === 'object' && items !== null ? Object.keys(items).length : 0,
    connectionsCount: typeof items === 'object' && items !== null
      ? Object.values(items).reduce((total, connections) =>
          total + (Array.isArray(connections) ? connections.length : 0), 0)
      : 0
  };

  return report;
}